import { useCommands, PositionerPortal, useChainedCommands, useEditorView } from '@remirror/react';
import TooltipButton from './../../../../cms/src/components/TooltipButton';
import { DeleteOutlined } from '@ant-design/icons';
import React, { useCallback, useState, useEffect } from 'react';

export const TableColumnRight = React.memo(() => (
  <span role="img" className="anticon ant-menu-item-icon">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" transform="translate(0 24) rotate(-90)" fill="none" fillOpacity="0.01"></rect>
      <rect x="9" y="18" width="12" height="3" rx="0.5" transform="rotate(-90 9 18)" fill="currentColor"></rect>
      <rect x="5" y="18" width="12" height="3" rx="0.5" transform="rotate(-90 5 18)" fill="currentColor"></rect>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 11L13.99 11C13.7265 11.0026 13.4747 11.1092 13.2893 11.2964C13.104 11.4837 13 11.7365 13 12C13 12.556 13.444 13 13.99 13L15 13L15 14C15 14.2652 15.1054 14.5196 15.2929 14.7071C15.4804 14.8946 15.7348 15 16 15C16.2652 15 16.5196 14.8946 16.7071 14.7071C16.8946 14.5196 17 14.2652 17 14L17 13L18.01 13C18.2735 12.9974 18.5253 12.8908 18.7107 12.7036C18.896 12.5163 19 12.2635 19 12C19 11.444 18.556 11 18.01 11L17 11L17 10C17 9.73478 16.8946 9.48043 16.7071 9.29289C16.5196 9.10536 16.2652 9 16 9C15.7348 9 15.4804 9.10536 15.2929 9.29289C15.1054 9.48043 15 9.73478 15 10L15 11Z"
        fill="currentColor"
      ></path>
    </svg>
  </span>
));

export const TableRowBelow = React.memo(() => (
  <span role="img" className="anticon ant-menu-item-icon">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        width="24"
        height="24"
        transform="matrix(-1 0 0 -1 24 24)"
        fill="var(--ds-border-inverse, #FFFFFF)"
        fillOpacity="0.01"
      ></rect>
      <rect x="18" y="12" width="12" height="3" rx="0.5" transform="rotate(-180 18 12)" fill="currentColor"></rect>
      <rect x="18" y="8" width="12" height="3" rx="0.5" transform="rotate(-180 18 8)" fill="currentColor"></rect>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 17V18.01C11.0026 18.2735 11.1092 18.5253 11.2964 18.7107C11.4837 18.896 11.7365 19 12 19C12.556 19 13 18.556 13 18.01V17H14C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16C15 15.7348 14.8946 15.4804 14.7071 15.2929C14.5196 15.1054 14.2652 15 14 15H13V13.99C12.9974 13.7265 12.8908 13.4747 12.7036 13.2893C12.5163 13.104 12.2635 13 12 13C11.444 13 11 13.444 11 13.99V15H10C9.73478 15 9.48043 15.1054 9.29289 15.2929C9.10536 15.4804 9 15.7348 9 16C9 16.2652 9.10536 16.5196 9.29289 16.7071C9.48043 16.8946 9.73478 17 10 17H11Z"
        fill="currentColor"
      ></path>
    </svg>
  </span>
));

export const DeleteTableColumn = React.memo(() => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-between-horizontal-end"
  >
    <rect width="13" height="7" x="3" y="3" rx="1" />
    <path d="m22 15-3-3 3-3" />
    <rect width="13" height="7" x="3" y="14" rx="1" />
  </svg>
));

export const DeleteTableRow = React.memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-between-vertical-start"
  >
    <rect width="7" height="13" x="3" y="8" rx="1" />
    <path d="m15 2-3 3-3-3" />
    <rect width="7" height="13" x="14" y="8" rx="1" />
  </svg>
));

const TablePopoverMenu = ({ position }) => {
  const [isMergeDisabled, setIsMergeDisabled] = useState(true); // Track if the Merge button should be disabled
  const commands = useChainedCommands();
  const { state, view } = useEditorView();
  const { customDispatch, toggleTableCellMerge } = useCommands();

  const handleClick = useCallback((command) => {
    command().focus().run();
    customDispatch((props) => {
      props.tr.setMeta('tableController', { preselectTable: false, predelete: false });
      return true;
    });
  }, []);

  useEffect(() => {
    const { selection } = state;
    const { from, to } = selection;
    // Disable the merge button if the selected range is less than 1 cell
    if (Math.abs(from - to) < 1) {
      setIsMergeDisabled(true);
    } else {
      setIsMergeDisabled(false);
    }
  }, [state]);

  return (
    <div
      ref={position.ref}
      className="table-bottom-popover-container"
      style={{
        position: 'absolute',
        top: `${position.y}px`,
        left: `50%`,
        transform: 'translate(-50%, 0)',
      }}
    >
      <TooltipButton
        type="text"
        tooltipText={'Add column right'}
        className="table-bottom-popover-button"
        key={'addColumnRight'}
        icon={<TableColumnRight />}
        onClick={() => handleClick(commands.addTableColumnAfter)}
      />
      <TooltipButton
        type="text"
        tooltipText={'Add row below'}
        className="table-bottom-popover-button"
        key={'addRowBelow'}
        icon={<TableRowBelow />}
        onClick={() => handleClick(commands.addTableRowAfter)}
      />
      <TooltipButton
        type="text"
        tooltipText={'Delete column'}
        className="table-bottom-popover-button"
        key={'deleteColumn'}
        icon={<DeleteTableColumn />}
        onClick={() => handleClick(commands.deleteTableColumn)}
      />
      <TooltipButton
        type="text"
        tooltipText={'Delete row'}
        className="table-bottom-popover-button"
        key={'deleteRow'}
        icon={<DeleteTableRow />}
        onClick={() => handleClick(commands.deleteTableRow)}
      />
      <TooltipButton
        type="text"
        tooltipText={'Delete table'}
        className="table-bottom-popover-button"
        key={'deleteTable'}
        icon={<DeleteOutlined />}
        onClick={() => handleClick(commands.deleteTable)}
      />
    </div>
  );
};

export const TableBottomPopover = ({ position, onClick }) => {
  return (
    <PositionerPortal>
      <TablePopoverMenu position={position} />
    </PositionerPortal>
  );
};

export default TableBottomPopover;
